// src/utils/weather.ts
import { callApi } from '@/utils/api'
import axios from 'axios'
import type {
  TimeInfo,
  WeatherDay,
  WeatherResponse,
  RouteWeatherResponse,
  UpperWeatherPoint,
  UpperWeatherResponse,
} from '@/types/weatherOverview'
import { formatApiDate } from '@/utils/common'

const BASE_URL = (import.meta.env.VITE_AAM_W_URL + import.meta.env.VITE_AAM_W_PREFIX) as string

export async function fetchTimeInfo(): Promise<TimeInfo> {
  return await callApi<TimeInfo>('/common/time.json', 'GET', undefined, BASE_URL)
}

// エリアの天気
export async function fetchAreaWeather(areaId: number, start: Date): Promise<WeatherDay[]> {
  try {
    // 開始：現在時刻
    const s = formatApiDate(start)

    // 終了：13日後
    const end = new Date(start)
    end.setDate(end.getDate() + 13)
    end.setHours(23, 59, 0, 0)
    const e = formatApiDate(end)

    const res = await callApi<WeatherResponse>(
      `/weather/${areaId}/${s},${e}.json`,
      'GET',
      undefined,
      BASE_URL,
    )

    return res.weathers ?? []
  } catch (e: unknown) {
    if (axios.isAxiosError(e)) {
      if (e.response?.data?.error_id === 'W1001') {
        return []
      }
    }
    throw e
  }
}

// 航路上の風況取得
export async function fetchRouteWeather(areaId: number, routeId: number, departureDate: Date) {
  // 出発日の 00:00
  const start = new Date(
    departureDate.getFullYear(),
    departureDate.getMonth(),
    departureDate.getDate(),
    0,
    0,
    0,
  )

  // 翌日 06:00
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  end.setHours(6, 0, 0, 0)

  const s = formatApiDate(start)
  const e = formatApiDate(end)

  const res = await callApi<RouteWeatherResponse>(
    `/wind/route/${areaId}/${routeId}/${s},${e}.json`,
    'GET',
    undefined,
    BASE_URL,
  )

  return res.routes ?? []
}

// 上空の風取得
export async function fetchUpperWind(
  areaId: number,
  routeId: number,
  departureDate: Date,
): Promise<UpperWeatherPoint[]> {
  // 出発日の 00:00
  const base = new Date(
    departureDate.getFullYear(),
    departureDate.getMonth(),
    departureDate.getDate(),
    0,
    0,
    0,
  )

  // 開始：前日 22:00
  const start = new Date(base)
  start.setHours(start.getHours() - 2)

  // 終了：翌日 06:00
  const end = new Date(base)
  end.setDate(end.getDate() + 1)
  end.setHours(6, 0, 0, 0)

  const s = formatApiDate(start)
  const e = formatApiDate(end)

  const res = await callApi<UpperWeatherResponse>(
    `/wind/profile/${areaId}/${routeId}/${s},${e}.json`,
    'GET',
    undefined,
    BASE_URL,
  )

  return res.points ?? []
}
